// Wave 17 — Admin audit log.
//
// Cursor-paginated read-only table over `/api/admin/audit`. Workspace
// admins (and owners) see every privileged mutation: campaign deletes,
// member revocations, ad-account-connection deletes, etc. Filters
// `action`, `target_type`, `actor_id`, and ISO date range narrow the
// page server-side. The "Load more" button feeds `next_cursor` back to
// keep paging stable even when new rows arrive.

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { useT } from '../i18n';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { RefreshCw, Download } from 'lucide-react';

const EMPTY_FILTERS = {
  action: '',
  target_type: '',
  actor_id: '',
  since: '',
  until: '',
};

function formatTimestamp(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function payloadPreview(payload) {
  if (!payload || typeof payload !== 'object') return '';
  if (payload.__truncated) {
    return '[truncated]';
  }
  const keys = Object.keys(payload);
  if (keys.length === 0) return '';
  try {
    const compact = JSON.stringify(payload);
    return compact.length > 140 ? compact.slice(0, 137) + '…' : compact;
  } catch {
    return '';
  }
}

export default function AdminAudit() {
  const { t } = useT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [exporting, setExporting] = useState(false);

  const exportFile = async (format) => {
    if (exporting) return;
    setExporting(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value.trim()) params[key] = value.trim();
      });
      const path = format === 'xlsx' ? 'export-xlsx' : 'export-csv';
      const fallback = format === 'xlsx' ? 'audit.xlsx' : 'audit.csv';
      const r = await axios.get(`${API}/admin/audit/${path}`, {
        params,
        responseType: 'blob',
      });
      // Honour the server-supplied filename when present so the
      // downloaded file is consistent across browsers.
      const cd = r.headers?.['content-disposition'] || '';
      const match = /filename=([^;]+)/i.exec(cd);
      const filename = (match && match[1].trim().replace(/^"|"$/g, '')) || fallback;
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t('Audit log exported'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('Audit export failed'));
    } finally {
      setExporting(false);
    }
  };
  const exportCsv = () => exportFile('csv');
  const exportXlsx = () => exportFile('xlsx');

  const fetchPage = async (opts = {}) => {
    const { append = false, cursor = null } = opts;
    if (append) {
      setLoading(true);
    } else {
      setLoading(true);
      setItems([]);
    }
    setError(null);
    try {
      const params = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value.trim()) params[key] = value.trim();
      });
      if (cursor) params.cursor = cursor;
      const r = await axios.get(`${API}/admin/audit`, { params });
      const incoming = Array.isArray(r.data?.items) ? r.data.items : [];
      setItems((prev) => (append ? [...prev, ...incoming] : incoming));
      setNextCursor(r.data?.next_cursor || null);
    } catch (e) {
      const detail = e?.response?.data?.detail || t('Failed to load audit log');
      setError(detail);
      if (!append) setItems([]);
      // 403 means the caller has no admin workspace — render the
      // detail inline rather than a toast, the empty state already
      // explains the situation.
      if (e?.response?.status !== 403) {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  // First page on mount; subsequent reloads happen via Apply / Reset / Load
  // more — we deliberately don't auto-reload on every keystroke so the
  // operator can compose a multi-field filter before hitting submit.
  useEffect(() => {
    fetchPage();
  }, []);

  const apply = () => fetchPage();
  const reset = () => {
    setFilters(EMPTY_FILTERS);
    setNextCursor(null);
    setTimeout(() => fetchPage(), 0);
  };
  const loadMore = () => {
    if (nextCursor) fetchPage({ append: true, cursor: nextCursor });
  };

  const setFilter = (key, value) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  return (
    <div className="xp-page">
      <Card data-testid="admin-audit-card">
        <CardHeader className="flex flex-row items-start justify-between gap-2">
          <div>
            <CardTitle>{t('Audit log')}</CardTitle>
            <CardDescription>
              {t(
                'Workspace-scoped trail of privileged actions. Retained for 90 days.',
              )}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={exportCsv}
              disabled={loading || exporting}
              data-testid="audit-export-csv"
            >
              <Download className={`w-4 h-4 mr-1 ${exporting ? 'animate-pulse' : ''}`} />
              {t('Export CSV')}
            </Button>
            <Button
              variant="outline"
              onClick={exportXlsx}
              disabled={loading || exporting}
              data-testid="audit-export-xlsx"
            >
              <Download className={`w-4 h-4 mr-1 ${exporting ? 'animate-pulse' : ''}`} />
              {t('Export XLSX')}
            </Button>
            <Button
              variant="outline"
              onClick={apply}
              disabled={loading}
              data-testid="audit-refresh"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              {t('Refresh')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
            <div>
              <Label htmlFor="audit-filter-action">{t('Action')}</Label>
              <Input
                id="audit-filter-action"
                placeholder="campaign.deleted"
                value={filters.action}
                onChange={(e) => setFilter('action', e.target.value)}
                data-testid="audit-filter-action"
              />
            </div>
            <div>
              <Label htmlFor="audit-filter-target-type">{t('Target type')}</Label>
              <Input
                id="audit-filter-target-type"
                placeholder="campaign"
                value={filters.target_type}
                onChange={(e) => setFilter('target_type', e.target.value)}
                data-testid="audit-filter-target-type"
              />
            </div>
            <div>
              <Label htmlFor="audit-filter-actor">{t('Actor ID')}</Label>
              <Input
                id="audit-filter-actor"
                placeholder="uuid"
                value={filters.actor_id}
                onChange={(e) => setFilter('actor_id', e.target.value)}
                data-testid="audit-filter-actor"
              />
            </div>
            <div>
              <Label htmlFor="audit-filter-since">{t('Since')}</Label>
              <Input
                id="audit-filter-since"
                type="datetime-local"
                value={filters.since}
                onChange={(e) => setFilter('since', e.target.value)}
                data-testid="audit-filter-since"
              />
            </div>
            <div>
              <Label htmlFor="audit-filter-until">{t('Until')}</Label>
              <Input
                id="audit-filter-until"
                type="datetime-local"
                value={filters.until}
                onChange={(e) => setFilter('until', e.target.value)}
                data-testid="audit-filter-until"
              />
            </div>
          </div>
          <div className="flex gap-2 mb-4">
            <Button onClick={apply} disabled={loading} data-testid="audit-apply">
              {t('Apply')}
            </Button>
            <Button
              variant="outline"
              onClick={reset}
              disabled={loading}
              data-testid="audit-reset"
            >
              {t('Reset')}
            </Button>
          </div>

          <Table data-testid="audit-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t('Time')}</TableHead>
                <TableHead>{t('Actor')}</TableHead>
                <TableHead>{t('Action')}</TableHead>
                <TableHead>{t('Target')}</TableHead>
                <TableHead>{t('Payload')}</TableHead>
                <TableHead>{t('IP')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((row) => (
                <TableRow key={row.id} data-testid={`audit-row-${row.id}`}>
                  <TableCell className="whitespace-nowrap font-mono text-xs">
                    {formatTimestamp(row.timestamp)}
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{row.actor_email || '—'}</div>
                    <div className="text-xs text-muted-foreground font-mono">
                      {row.actor_id}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{row.action}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">{row.target_type}</div>
                    {row.target_id ? (
                      <div className="text-xs text-muted-foreground font-mono">
                        {row.target_id}
                      </div>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-xs font-mono break-all max-w-[280px]">
                    {payloadPreview(row.payload)}
                  </TableCell>
                  <TableCell className="text-xs font-mono">
                    {row.ip || '—'}
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && !loading && (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-center text-muted-foreground"
                  >
                    {error
                      ? error
                      : t('No audit entries match the current filters.')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {nextCursor ? (
            <div className="mt-3 flex justify-center">
              <Button
                variant="outline"
                onClick={loadMore}
                disabled={loading}
                data-testid="audit-load-more"
              >
                {loading ? t('Loading…') : t('Load more')}
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
